import type { Component, Ref } from "vue";

import columns from "~/components/visualization/list/columns";
import type { Comparator, ExtendedColDef, GroupByDefinition, PropertyValue, Sort, Task } from "~/shared/types";
import useAppStore from "~/stores/AppStore";
import type { DataStore } from "~/stores/DataStore";
import { makeListComparatorOrganizedBySize, makeTaskComparator, numberComparator } from "~/utils/comparator";

import { NO_VALUE_GROUP_BY_KEY } from "./groupBy";
import { getPropertyValueFromTaskByDuid, getPropertyValueStr } from "./properties";

export const NO_SORT = "";
export const ASCENDING = "asc";
export const DESCENDING = "desc";

export type SortItem = {
  duid: string;
  title: string;
  icon: Component;
  isAscending: boolean;
  propertyDuid: string;
  order: string;
};

export type SortDirection = typeof NO_SORT | typeof ASCENDING | typeof DESCENDING;

export const SORT_TO_NEXT_SORT_MAP: Map<SortDirection, SortDirection> = new Map([
  [NO_SORT, ASCENDING],
  [ASCENDING, DESCENDING],
  [DESCENDING, NO_SORT],
]);

export const sortDirection = (propertyDuid: string) => {
  const appStore = useAppStore();
  const sort = appStore.sorts.find((e) => e.propertyDuid === propertyDuid);
  if (!sort) {
    return NO_SORT;
  }
  return sort.isAscending ? ASCENDING : DESCENDING;
};

export const setSort = (newSort: SortDirection, propertyDuid: string) => {
  const appStore = useAppStore();
  if (newSort === NO_SORT) {
    appStore.setAllSorts(appStore.sorts.filter((e) => e.propertyDuid !== propertyDuid));
    return;
  }
  const index = appStore.sorts.findIndex((e) => e.propertyDuid === propertyDuid);
  const sorts = [...appStore.sorts];
  const sortConfig = { propertyDuid, isAscending: newSort === ASCENDING };

  if (index >= 0) {
    sorts[index] = sortConfig;
  } else {
    sorts.push(sortConfig);
  }
  appStore.setAllSorts(sorts);
};

export const sortIndex = (propertyDuid: string) => {
  const appStore = useAppStore();
  const index = appStore.sorts.findIndex((e) => e.propertyDuid === propertyDuid);
  return index >= 0 ? index + 1 : null;
};

// TODO incremental refactor this and any similar logic in the app store to be reactive. unless it is already--how?
// probably best to refactor more thoroughly, not just add a watcher that recalculates the cache on every change. if we need to do that it's probably better to just get rid of the cache and return a computed or something.
let cachedColumns: Ref<ExtendedColDef[]>;
export const sortTasks = (
  dataStore: DataStore,
  tasks: Task[],
  sorts: Sort[],
  groupByDefinition: GroupByDefinition | null,
  isFlat: boolean
) => {
  if (!cachedColumns) {
    cachedColumns = columns(dataStore).combinedColumnDefs;
  }

  // Prepare group sorting data if needed
  let groupOptionOrder: Map<string, number> | undefined;
  let groupPropertyValues: Map<string, PropertyValue | PropertyValue[] | null | undefined> | undefined;
  if (groupByDefinition) {
    groupOptionOrder = new Map(groupByDefinition.groups.map(({ value }, index) => [getPropertyValueStr(value), index]));
    groupPropertyValues = new Map(
      tasks.map((e) => [e.duid, getPropertyValueFromTaskByDuid(groupByDefinition.property.duid, e)])
    );
  }

  const sortsAndComparators: [Sort, Comparator][] = sorts
    .map((sort) => [sort, cachedColumns.value.find((e) => e.field === sort.propertyDuid)?.comparatorFn])
    .filter((e): e is [Sort, Comparator] => !!e[1]);

  const taskComparator = makeTaskComparator(dataStore.getSpaceByDuid, dataStore.getDartboardByDuid);

  tasks.sort((a, b) => {
    // First, apply group sorting if applicable
    if (groupByDefinition && groupOptionOrder && groupPropertyValues) {
      const propertyValueA = groupPropertyValues.get(a.duid);
      const propertyValueB = groupPropertyValues.get(b.duid);

      if (propertyValueA !== undefined && propertyValueB !== undefined) {
        const propertyValueArr = Array.isArray(propertyValueA)
          ? propertyValueA.length === 0
            ? [NO_VALUE_GROUP_BY_KEY]
            : propertyValueA
          : [propertyValueA];
        const propertyValueArrB = Array.isArray(propertyValueB)
          ? propertyValueB.length === 0
            ? [NO_VALUE_GROUP_BY_KEY]
            : propertyValueB
          : [propertyValueB];

        const aMinOrder = Math.min(
          ...propertyValueArr.map(
            (value) => groupOptionOrder.get(getPropertyValueStr(value as PropertyValue)) ?? groupOptionOrder.size
          )
        );
        const bMinOrder = Math.min(
          ...propertyValueArrB.map(
            (value) => groupOptionOrder.get(getPropertyValueStr(value as PropertyValue)) ?? groupOptionOrder.size
          )
        );

        const groupResult = aMinOrder - bMinOrder;
        if (groupResult !== 0) {
          return groupResult;
        }
      }
    }

    // Apply user-defined sorts
    let sortResult = 0;
    sortsAndComparators.some(([sort, comparator]) => {
      const aValue = getPropertyValueFromTaskByDuid(sort.propertyDuid, a);
      const bValue = getPropertyValueFromTaskByDuid(sort.propertyDuid, b);

      const aIsEmpty =
        aValue === null ||
        aValue === undefined ||
        (Array.isArray(aValue) && (aValue.length === 0 || aValue.every((e) => e === null)));
      const bIsEmpty =
        bValue === null ||
        bValue === undefined ||
        (Array.isArray(bValue) && (bValue.length === 0 || bValue.every((e) => e === null)));

      if (aIsEmpty || bIsEmpty) {
        if (!(aIsEmpty && bIsEmpty)) {
          sortResult = aIsEmpty ? 1 : -1;
          return true;
        }
        return false;
      }

      const res = comparator(aValue, bValue);
      if (res !== 0) {
        sortResult = sort.isAscending ? res : -res;
        return true;
      }
      return false;
    });

    if (sortResult !== 0) {
      return sortResult;
    }

    // Apply default task comparator
    return taskComparator(a, b);
  });

  if (isFlat) {
    return tasks;
  }

  const ancestorMap = new Map(tasks.map((e) => [e.duid, dataStore.getAncestorDuidsIfLoaded(e)]));
  const taskIndexMap = new Map(tasks.map((task, index) => [task.duid, index]));
  const fullOrderComparator = makeListComparatorOrganizedBySize(numberComparator);

  // TODO is there a way to one-pass this with the sort above?
  tasks.sort((a, b) => {
    const aAncestors = ancestorMap.get(a.duid) ?? [];
    const bAncestors = ancestorMap.get(b.duid) ?? [];
    const aFullOrder = [...aAncestors.map((f) => taskIndexMap.get(f) ?? 0), taskIndexMap.get(a.duid) ?? 0];
    const bFullOrder = [...bAncestors.map((f) => taskIndexMap.get(f) ?? 0), taskIndexMap.get(b.duid) ?? 0];

    return fullOrderComparator(aFullOrder, bFullOrder);
  });

  return tasks;
};
